import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RevenueChart = () => {
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('6months');

  const revenueData = [
    { month: 'Jan', revenue: 45200, payments: 156, outstanding: 8400 },
    { month: 'Feb', revenue: 52800, payments: 178, outstanding: 7200 },
    { month: 'Mar', revenue: 48600, payments: 164, outstanding: 9100 },
    { month: 'Apr', revenue: 61200, payments: 201, outstanding: 6800 },
    { month: 'May', revenue: 58900, payments: 189, outstanding: 7500 },
    { month: 'Jun', revenue: 67400, payments: 223, outstanding: 5900 },
    { month: 'Jul', revenue: 72100, payments: 245, outstanding: 4800 },
    { month: 'Aug', revenue: 69800, payments: 234, outstanding: 6200 },
    { month: 'Sep', revenue: 75600, payments: 267, outstanding: 4100 },
    { month: 'Oct', revenue: 81200, payments: 289, outstanding: 3600 },
    { month: 'Nov', revenue: 78900, payments: 276, outstanding: 4900 },
    { month: 'Dec', revenue: 84500, payments: 298, outstanding: 3200 }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-text-primary mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-text-muted">{entry.name}:</span>
              <span className="font-medium text-text-primary">
                {entry.name === 'Revenue' || entry.name === 'Outstanding' 
                  ? `$${entry.value.toLocaleString()}` 
                  : entry.value.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-text-primary">Revenue Analytics</h3>
          <p className="text-sm text-text-muted">Track revenue trends and payment patterns</p>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Time Range Selector */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
          >
            <option value="3months">Last 3 Months</option>
            <option value="6months">Last 6 Months</option>
            <option value="12months">Last 12 Months</option>
            <option value="24months">Last 24 Months</option>
          </select>

          {/* Chart Type Toggle */}
          <div className="flex items-center bg-background border border-border rounded-lg p-1">
            <Button
              variant={chartType === 'line' ? 'primary' : 'ghost'}
              onClick={() => setChartType('line')}
              className="p-2"
            >
              <Icon name="TrendingUp" size={16} />
            </Button>
            <Button
              variant={chartType === 'bar' ? 'primary' : 'ghost'}
              onClick={() => setChartType('bar')}
              className="p-2"
            >
              <Icon name="BarChart3" size={16} />
            </Button>
          </div>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis 
                dataKey="month" 
                stroke="#a3a3a3"
                fontSize={12}
              />
              <YAxis 
                stroke="#a3a3a3"
                fontSize={12}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
              />
              <Line
                type="monotone"
                dataKey="outstanding"
                name="Outstanding"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ fill: '#ef4444', strokeWidth: 2, r: 3 }}
                strokeDasharray="5 5"
              />
            </LineChart>
          ) : (
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis 
                dataKey="month" 
                stroke="#a3a3a3"
                fontSize={12}
              />
              <YAxis 
                stroke="#a3a3a3"
                fontSize={12}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="revenue"
                name="Revenue"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="outstanding"
                name="Outstanding"
                fill="#ef4444"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Chart Legend */}
      <div className="flex items-center justify-center space-x-6 mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-accent rounded-full"></div>
          <span className="text-sm text-text-muted">Revenue</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-error rounded-full"></div>
          <span className="text-sm text-text-muted">Outstanding Balance</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="TrendingUp" size={14} className="text-success" />
          <span className="text-sm text-text-muted">Growth Trend</span>
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;